/**
 * 详情页布局组件
 *
 * @experimental 此组件尚未在消费项目中广泛使用，API 可能调整。
 *
 * 适用于详情/表单页（create/edit/view 模式）。
 * 如果是列表页容器，请使用 {@link PageContainer}。
 *
 * 封装三个项目中通用的详情/表单页布局模式:
 * - 面包屑导航
 * - 页面标题 + 副标题 + 操作按钮
 * - 内容区域 (Card 包裹)
 * - 支持 create / edit / view 三种模式
 * - 返回按钮
 */

import { ArrowLeftOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Card, Space, Spin, theme } from 'antd';
import type React from 'react';

// ===== 类型定义 =====

/**
 * 页面模式
 */
export type DetailPageMode = 'create' | 'edit' | 'view';

/**
 * 详情页面包屑项
 */
export interface DetailBreadcrumbItem {
  /** 显示文本 */
  title: string;
  /** 链接路径 */
  path?: string;
}

/**
 * DetailPageLayout 属性
 */
export interface DetailPageLayoutProps {
  /** 页面模式 */
  mode?: DetailPageMode;
  /** 页面标题 (不传则根据 mode 自动生成) */
  title?: React.ReactNode;
  /** 副标题 */
  subtitle?: React.ReactNode;
  /** 面包屑项 */
  breadcrumb?: DetailBreadcrumbItem[];
  /** 右上角操作按钮区域 */
  actions?: React.ReactNode;
  /** 底部操作栏 (如保存/取消按钮) */
  footer?: React.ReactNode;
  /** 是否显示返回按钮, 默认 true */
  showBack?: boolean;
  /** 返回按钮点击回调 */
  onBack?: () => void;
  /** 返回按钮的无障碍标签，默认 '返回' */
  backAriaLabel?: string;
  /** 面包屑导航回调 (传入 path) */
  onNavigate?: (path: string) => void;
  /** 是否加载中 */
  loading?: boolean;
  /** 自定义类名 */
  className?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 子内容 */
  children: React.ReactNode;
}

// ===== 模式标题映射 =====

const MODE_TITLES: Record<DetailPageMode, string> = {
  create: '新建',
  edit: '编辑',
  view: '详情',
};

// ===== 组件实现 =====

/**
 * 详情页布局组件
 *
 * @example
 * ```tsx
 * // 基础用法 — 编辑页
 * <DetailPageLayout
 *   mode="edit"
 *   title="编辑用户"
 *   breadcrumb={[
 *     { title: '用户管理', path: '/users' },
 *     { title: '编辑' },
 *   ]}
 *   onBack={() => history.back()}
 *   footer={
 *     <Space>
 *       <Button onClick={() => history.back()}>取消</Button>
 *       <Button type="primary" loading={submitting} onClick={handleSave}>保存</Button>
 *     </Space>
 *   }
 * >
 *   <Form form={form} layout="vertical">
 *     ...
 *   </Form>
 * </DetailPageLayout>
 *
 * // 详情页 (只读)
 * <DetailPageLayout
 *   mode="view"
 *   title="用户详情"
 *   actions={<Button onClick={() => goEdit(id)}>编辑</Button>}
 *   loading={loading}
 * >
 *   <Descriptions>...</Descriptions>
 * </DetailPageLayout>
 * ```
 */
export function DetailPageLayout(props: DetailPageLayoutProps) {
  const { token } = theme.useToken();
  const {
    mode = 'view',
    title,
    subtitle,
    breadcrumb,
    actions,
    footer,
    showBack = true,
    onBack,
    backAriaLabel = '返回',
    onNavigate,
    loading = false,
    className,
    style,
    children,
  } = props;

  const displayTitle = title ?? MODE_TITLES[mode];

  // 构建 antd Breadcrumb items
  const breadcrumbItems = breadcrumb?.map((item, index) => {
    const isLast = index === breadcrumb.length - 1;
    if (item.path && !isLast) {
      const navigatePath = item.path;
      return {
        key: `${index}-${item.title}`,
        title: (
          <a
            href={navigatePath}
            onClick={(e) => {
              e.preventDefault();
              if (onNavigate) {
                onNavigate(navigatePath);
              } else {
                onBack?.();
              }
            }}
          >
            {item.title}
          </a>
        ),
      };
    }
    return { key: `${index}-${item.title}`, title: item.title };
  });

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: token.marginMD,
        ...style,
      }}
    >
      {/* 面包屑 */}
      {breadcrumbItems && breadcrumbItems.length > 0 && (
        <Breadcrumb items={breadcrumbItems} />
      )}

      {/* 页面头部 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: token.marginSM,
        }}
      >
        <Space align="center">
          {showBack && onBack && (
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              aria-label={backAriaLabel}
            />
          )}
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading4,
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              {displayTitle}
            </h2>
            {subtitle && (
              <div
                style={{
                  fontSize: token.fontSize,
                  color: token.colorTextTertiary,
                  marginTop: token.marginXXS,
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
        </Space>
        {actions && <Space>{actions}</Space>}
      </div>

      {/* 内容区域 */}
      <Spin spinning={loading}>
        <Card>{children}</Card>
      </Spin>

      {/* 底部操作栏 */}
      {footer && (
        <Card size="small">
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: token.marginXS,
            }}
          >
            {footer}
          </div>
        </Card>
      )}
    </div>
  );
}
